import { components } from "@certn/api/generated-schemas/client-portal";
import {
  Avatar,
  CopyToClipboard,
  DialogTrigger,
  NeutralTag,
  Text,
  useColors,
} from "@certn/cascadia-react";
import { formatDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { NameTableCell } from "./NameTableCell";
import { StatusPopover } from "./StatusPopover";
import {
  useCanViewCaseScore,
  useCanViewCaseDetails,
} from "../../../components/permissions/Permissions";
import { renderStatusTag } from "../../../utils/renderStatusTag";

type TableDataCardProps = {
  data: components["schemas"]["BaseCaseList"];
};

export function TableDataCard(props: TableDataCardProps) {
  const { data } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const colors = useColors();
  const { clientPortalCaseTags, clientPortalSplitUpCaseListEndpoint } =
    useFlags();
  const canViewScore = useCanViewCaseScore();
  const canViewCaseDetails = useCanViewCaseDetails();

  return (
    <section
      role="presentation"
      className={css([
        styles.container,
        {
          border: `1px solid ${colors["border-base"]}`,
          borderRadius: "var(--border-radius-3)",
        },
      ])}
      onClick={
        canViewCaseDetails
          ? () => {
              const isInDraft = data.overall_status?.name === "DRAFT";

              if (isInDraft) {
                navigate(`/quickscreen/${data.short_id}`);
              } else {
                navigate(`/case-details/${data.short_id}`, {
                  state: {
                    last: { href: `${location.pathname}${location.search}` },
                  },
                });
              }
            }
          : undefined
      }
      style={canViewCaseDetails ? undefined : { cursor: "default" }}
      data-testid="table-data-card"
    >
      <div className={styles.firstRow}>
        <div className={styles.firstRowChildrenOne}>
          <DialogTrigger>
            {renderStatusTag({
              status: data.overall_status,
              decision: data.decision,
              score: data.overall_score,
              hasPopover: true,
              isScoreHidden: !canViewScore,
            })}
            <StatusPopover
              data={
                !clientPortalSplitUpCaseListEndpoint
                  ? data.popover_details
                  : undefined
              }
              status={
                data.overall_status as components["schemas"]["ClientPortalOverallStatus"]
              }
              decision={
                data.decision as components["schemas"]["ClientPortalDecision"]
              }
              caseId={data.short_id}
              created={data.created}
              isCaseNavigationHidden={!canViewCaseDetails}
            />
          </DialogTrigger>
        </div>
        <div className={styles.firstRowChildrenTwo}>
          <Avatar
            name={parseName(data?.ordered_by?.auth0_full_name || undefined)}
          />
          {(canViewCaseDetails || data.can_resend_invite) && (
            <div
              role="presentation"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <ActionsDropdown
                id={data.short_id}
                applicant={[
                  data.applicant_details?.current_name?.given_name,
                  data.applicant_details?.current_name?.family_name,
                ]
                  .filter((part) => !!part && part.trim().length > 0)
                  .join(" ")}
                applicantEmail={
                  data.applicant_details?.email_address ||
                  (
                    data.applicant_details as
                      | { invite_email_address?: string }
                      | undefined
                  )?.invite_email_address ||
                  ""
                }
                status={
                  data.overall_status as components["schemas"]["ClientPortalOverallStatus"]
                }
                canResendInvite={data.can_resend_invite === true}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.secondRow}>
        <NameTableCell
          name={data?.applicant_details?.current_name}
          email={data?.applicant_details?.email_address}
          linkedCases={
            data.primary_case
              ? [...data?.linked_cases, data.primary_case]
              : [...data?.linked_cases]
          }
          isMobile
          isApplicantPay={data.is_applicant_pay}
        />
      </div>
      {data.package?.name && (
        <div className={styles.package}>
          <Text style="global/label-small" color="text-weak">
            {t("Package")}
          </Text>
          <Text style="global/body-secondary" color="text-base">
            {data.package.name}
          </Text>
        </div>
      )}
      {clientPortalCaseTags && (
        <div className={styles.tags}>
          {data?.tag_set.map((tag, i) => {
            return (
              <NeutralTag type="subtle" key={i}>
                {tag.name}
              </NeutralTag>
            );
          })}
        </div>
      )}
      <div className={styles.thirdRow}>
        <div className={styles.thirdRowChildren}>
          <Text style="global/label-small" color="text-weak">
            {t("Issued")}
          </Text>
          <Text style="global/body-secondary" color="text-base">
            {formatDate(data.created) || t("Not available")}
          </Text>
        </div>
        <div className={styles.thirdRowChildren}>
          <Text style="global/label-small" color="text-weak">
            {t("Case ID")}
          </Text>
          <Text style="global/body-secondary" color="text-base">
            <CopyToClipboard text={data.short_id} testID="copyable-case-id" />
          </Text>
        </div>
      </div>
    </section>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    padding: "var(--space-3)",
    cursor: "pointer",
  }),
  firstRow: css({ display: "flex", width: "100%" }),
  firstRowChildrenOne: css({ display: "flex", alignItems: "center" }),
  firstRowChildrenTwo: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    flex: "auto",
    alignItems: "center",
    columnGap: "var(--space-2)",
  }),
  secondRow: css({
    width: "100%",
    textAlign: "left",
  }),
  thirdRow: css({
    display: "flex",
    flexDirection: "row",
    textAlign: "left",
    width: "100%",
  }),
  thirdRowChildren: css({
    display: "flex",
    flexDirection: "column",
    flex: "0 1 160px",
  }),
  package: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  tags: css({
    display: "flex",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  }),
};
